
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, Facebook, Twitter, MessageCircle, Mail, Link2, QrCode, Users, Eye, MapPin, Star, Calendar, Download, Instagram, Share, ExternalLink, Camera } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface ShareCollectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  collection: {
    id: string;
    name: string;
    description?: string;
    created_by_user?: {
      username?: string;
      avatar_url?: string;
    };
    followers_count?: number;
    is_public?: boolean;
    places_count?: number;
    city?: string;
    country?: string;
    place_items?: Array<{
      id: string;
      name: string;
      place_type?: string;
      photos?: string[];
      rating?: number;
      address?: string;
    }>;
    created_at?: string;
  };
}

export const ShareCollectionDialog: React.FC<ShareCollectionDialogProps> = ({
  isOpen,
  onClose,
  collection
}) => {
  const [copied, setCopied] = useState(false);
  const [isGeneratingStoryImage, setIsGeneratingStoryImage] = useState(false);
  const { toast } = useToast();

  // Debug logging when component receives props
  React.useEffect(() => {
    if (isOpen && collection) {
      console.log('ShareCollectionDialog received collection:', {
        id: collection.id,
        name: collection.name,
        places_count: collection.places_count,
        place_items_length: collection.place_items?.length,
        place_items: collection.place_items
      });
    }
  }, [isOpen, collection]);

  const shareUrl = `${window.location.origin}/share/collection/${collection.id}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  
  // Use place_items length as the primary source of truth for count
  const actualPlacesCount = collection.place_items?.length || 0;
  
  // Enhanced title and description for sharing
  const shareTitle = `${collection.name} - ${actualPlacesCount} Amazing Places${collection.city ? ` in ${collection.city}` : ''} | Travilis Collection`;
  const shareDescription = collection.description 
    ? `${collection.description} | Explore ${actualPlacesCount} handpicked places curated by ${collection.created_by_user?.username || 'a local expert'}.`
    : `Discover ${actualPlacesCount} amazing places in ${collection.city || 'this destination'} - a carefully curated travel collection with local insights and recommendations.`;
  
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedDescription = encodeURIComponent(shareDescription);

  // Get featured places for preview
  const featuredPlaces = collection.place_items?.slice(0, 6) || [];
  const hasMorePlaces = (collection.place_items?.length || 0) > 6;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: "Link copied!",
        description: "Share link has been copied to your clipboard.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually.",
        variant: "destructive",
      });
    }
  };

  // Load image from URL with CORS handling
  const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => {
        // Fallback: try without CORS
        const fallbackImg = new Image();
        fallbackImg.onload = () => resolve(fallbackImg);
        fallbackImg.onerror = reject;
        fallbackImg.src = url;
      };
      img.src = url;
    });
  };

  // Enhanced story image generation with one big hero photo and premium effects
  const generateStoryImage = async (): Promise<Blob | null> => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // Instagram Stories dimensions (9:16 ratio)
      canvas.width = 1080;
      canvas.height = 1920;

      // Hero image dimensions and positioning (defined at function scope)
      const heroWidth = canvas.width * 0.85;
      const heroHeight = heroWidth * 0.75;
      const heroX = (canvas.width - heroWidth) / 2;
      const heroY = 200;

      // Full Travilis purple background with depth
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#8B5CF6');
      gradient.addColorStop(0.2, '#7C3AED');
      gradient.addColorStop(0.5, '#6D28D9');
      gradient.addColorStop(0.8, '#7C3AED');
      gradient.addColorStop(1, '#8B5CF6');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add sophisticated texture overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
      for (let i = 0; i < 50; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const radius = Math.random() * 20 + 5;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Find the best photo from the collection
      const placesWithPhotos = featuredPlaces.filter(place => place.photos && place.photos.length > 0);
      
      if (placesWithPhotos.length > 0) {
        try {
          // Load the hero image (first available photo)
          const heroImageUrl = placesWithPhotos[0].photos![0];
          const heroImage = await loadImageFromUrl(heroImageUrl);

          // Create dramatic shadow for the hero image
          ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
          ctx.shadowBlur = 40;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 20;

          // Draw the hero image with rounded corners and premium frame
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(heroX, heroY, heroWidth, heroHeight, 30);
          ctx.clip();
          
          // Enhanced image filters for Instagram-worthy look
          ctx.filter = 'brightness(1.2) contrast(1.15) saturate(1.3) blur(0px)';
          
          // Calculate proper scaling to fill the rounded rectangle
          const aspectRatio = heroImage.width / heroImage.height;
          const targetAspectRatio = heroWidth / heroHeight;
          
          let drawWidth, drawHeight, drawX, drawY;
          if (aspectRatio > targetAspectRatio) {
            drawHeight = heroHeight;
            drawWidth = drawHeight * aspectRatio;
            drawX = heroX - (drawWidth - heroWidth) / 2;
            drawY = heroY;
          } else {
            drawWidth = heroWidth;
            drawHeight = drawWidth / aspectRatio;
            drawX = heroX;
            drawY = heroY - (drawHeight - heroHeight) / 2;
          }
          
          ctx.drawImage(heroImage, drawX, drawY, drawWidth, drawHeight);
          ctx.filter = 'none';
          ctx.restore();

          // Reset shadow
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          // Premium glass morphism border around hero image
          const borderGradient = ctx.createLinearGradient(heroX, heroY, heroX + heroWidth, heroY + heroHeight);
          borderGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
          borderGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)');
          borderGradient.addColorStop(1, 'rgba(255, 255, 255, 0.9)');
          
          ctx.strokeStyle = borderGradient;
          ctx.lineWidth = 6;
          ctx.beginPath();
          ctx.roundRect(heroX, heroY, heroWidth, heroHeight, 30);
          ctx.stroke();

          // Add inner glow effect
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(heroX + 3, heroY + 3, heroWidth - 6, heroHeight - 6, 27);
          ctx.stroke();

        } catch (error) {
          console.log('Hero image failed to load, continuing with enhanced text design');
        }
      }

      // Enhanced typography positioning based on whether we have a hero image
      const titleY = placesWithPhotos.length > 0 ? heroY + heroHeight + 120 : 600;
      
      // Title with enhanced readability and premium styling
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 76px system-ui, -apple-system, "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.letterSpacing = '-2px';
      
      // Premium text shadow with multiple layers
      ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
      ctx.shadowBlur = 30;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
      
      // Smart word wrapping with better spacing
      const maxWidth = canvas.width - 120;
      const words = collection.name.split(' ');
      let line = '';
      let y = titleY;
      const lineHeight = 90;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line.trim(), canvas.width / 2, y);
          line = words[n] + ' ';
          y += lineHeight;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line.trim(), canvas.width / 2, y);

      // Enhanced location and stats with premium styling
      y += 130;
      ctx.font = '42px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.shadowBlur = 25;
      
      const statsText = `✨ ${actualPlacesCount} curated places${collection.city ? ` • 📍 ${collection.city}` : ''}`;
      ctx.fillText(statsText, canvas.width / 2, y);

      // Creator badge with enhanced glass morphism styling
      if (collection.created_by_user?.username) {
        y += 90;
        ctx.font = '34px system-ui, -apple-system, sans-serif';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.shadowBlur = 20;
        
        // Enhanced background badge for creator
        const creatorText = `👤 Curated by ${collection.created_by_user.username}`;
        const textMetrics = ctx.measureText(creatorText);
        const badgeWidth = textMetrics.width + 80;
        const badgeHeight = 70;
        const badgeX = (canvas.width - badgeWidth) / 2;
        const badgeY = y - 50;
        
        // Premium badge background with glass morphism
        const badgeGradient = ctx.createLinearGradient(badgeX, badgeY, badgeX + badgeWidth, badgeY + badgeHeight);
        badgeGradient.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
        badgeGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.15)');
        badgeGradient.addColorStop(1, 'rgba(255, 255, 255, 0.25)');
        
        ctx.fillStyle = badgeGradient;
        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 35);
        ctx.fill();
        
        // Badge border with enhanced glow
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Creator text
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.fillText(creatorText, canvas.width / 2, y);
      }

      // Enhanced app branding with sophisticated design
      const brandingY = canvas.height - 280;
      
      // Travilis brand text with premium styling
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 64px system-ui, -apple-system, "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.letterSpacing = '4px';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
      ctx.shadowBlur = 35;
      ctx.shadowOffsetY = 12;
      ctx.fillText('travilis', canvas.width / 2, brandingY + 40);
      
      // Enhanced tagline with better positioning
      ctx.font = '32px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.letterSpacing = '2px';
      ctx.shadowBlur = 20;
      ctx.fillText('🗺️ Your next adventure awaits', canvas.width / 2, brandingY + 90);

      // Call to action with premium styling
      ctx.font = '26px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.shadowBlur = 15;
      ctx.fillText('Check link in bio or swipe up for details', canvas.width / 2, canvas.height - 120);

      // Reset all effects
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.filter = 'none';
      ctx.letterSpacing = '0px';

      // Convert to blob with high quality
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 0.95);
      });
    } catch (error) {
      console.error('Error generating enhanced story image:', error);
      return null;
    }
  };

  // Native sharing with story image generation for all platforms
  const handleNativeShare = async () => {
    setIsGeneratingStoryImage(true);
    
    try {
      const imageBlob = await generateStoryImage();
      
      if (navigator.share) {
        const shareData: any = {
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        };

        // Try to include the image if possible
        if (imageBlob && navigator.canShare && navigator.canShare({ files: [new File([imageBlob], 'story.png', { type: 'image/png' })] })) {
          const file = new File([imageBlob], 'collection-story.png', { type: 'image/png' });
          shareData.files = [file];
        }

        await navigator.share(shareData);
        
        // Show specific instructions for Instagram
        toast({
          title: "Shared successfully!",
          description: "For Instagram Stories: Add a link sticker or mention the link in your bio for viewers to access the collection.",
          duration: 8000,
        });
      } else {
        // Fallback: download image and copy link
        if (imageBlob) {
          downloadAndInstructShare(imageBlob);
        } else {
          await handleCopyLink();
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        await handleCopyLink();
      }
    } finally {
      setIsGeneratingStoryImage(false);
    }
  };

  // Updated Reddit sharing with better UX for desktop
  const handleRedditShare = async () => {
    setIsGeneratingStoryImage(true);
    
    try {
      // First copy the link
      await navigator.clipboard.writeText(shareUrl);
      
      const imageBlob = await generateStoryImage();
      
      if (imageBlob) {
        // Show toast with option to download image
        toast({
          title: "Link copied! Image ready",
          description: "Reddit link copied to clipboard. Click below to download the image for your post.",
          duration: 10000,
          action: (
            <Button
              size="sm"
              onClick={() => {
                const url = URL.createObjectURL(imageBlob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${collection.name}-reddit.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                toast({
                  title: "Image downloaded!",
                  description: "Perfect for your Reddit post!",
                });
              }}
            >
              <Download className="w-4 h-4 mr-1" />
              Download Image
            </Button>
          ),
        });
        
        // Also open Reddit in a new tab
        setTimeout(() => {
          const redditUrl = `https://www.reddit.com/submit?title=${encodedTitle}`;
          window.open(redditUrl, '_blank', 'width=600,height=400');
        }, 1000);
      } else {
        // Fallback to regular Reddit share
        const redditUrl = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
        window.open(redditUrl, '_blank', 'width=600,height=400');
        
        toast({
          title: "Reddit opened!",
          description: "Link copied to clipboard.",
        });
      }
    } catch (error) {
      console.error('Error with Reddit sharing:', error);
      // Fallback to regular Reddit share
      const redditUrl = `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
      window.open(redditUrl, '_blank', 'width=600,height=400');
      
      toast({
        title: "Reddit opened",
        description: "Please copy the link manually if needed.",
      });
    } finally {
      setIsGeneratingStoryImage(false);
    }
  };

  const downloadAndInstructShare = (imageBlob: Blob) => {
    // Create download link
    const url = URL.createObjectURL(imageBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${collection.name}-story.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Copy link to clipboard
    handleCopyLink();

    // Show instructions with Instagram-specific guidance
    toast({
      title: "Story image downloaded!",
      description: "Perfect for sharing! For Instagram: Add a link sticker or mention 'link in bio' so followers can find the collection.",
      duration: 8000,
    });
  };

  // Simple Reddit icon component
  const RedditIcon = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.248 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
    </svg>
  );

  const shareOptions = [
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
      color: 'bg-blue-600 hover:bg-blue-700',
    },
    {
      name: 'Twitter',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      color: 'bg-black hover:bg-gray-800',
    },
    {
      name: 'Instagram',
      icon: Instagram,
      action: 'native',
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      color: 'bg-green-600 hover:bg-green-700',
    },
    {
      name: 'Reddit',
      icon: RedditIcon,
      action: 'reddit',
      color: 'bg-orange-600 hover:bg-orange-700',
    },
    {
      name: 'Email',
      icon: Mail,
      url: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0AExplore this collection:%0A${encodedUrl}`,
      color: 'bg-gray-600 hover:bg-gray-700',
    }
  ];

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-4">
          <DialogTitle className="text-2xl font-bold">Share Collection</DialogTitle>
          
          {/* Spotify-style Collection Preview */}
          <div className="bg-gradient-to-br from-primary/20 via-purple-500/10 to-pink-500/10 rounded-3xl p-6 border border-primary/20 shadow-2xl">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Collection Cover */}
              <div className="flex-shrink-0 flex justify-center lg:justify-start">
                <div className="w-48 h-48 lg:w-64 lg:h-64 bg-gradient-to-br from-primary to-purple-600 rounded-2xl shadow-2xl overflow-hidden border-4 border-white/20">
                  {featuredPlaces[0]?.photos?.[0] ? (
                    <img 
                      src={featuredPlaces[0].photos[0]} 
                      alt={collection.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <QrCode className="w-12 h-12 text-primary/60" />
                    </div>
                  )}
                </div>
              </div>

              {/* Collection Info */}
              <div className="flex-grow min-w-0 space-y-4">
                <div>
                  <Badge variant="secondary" className="mb-2 bg-white/20 text-primary border-primary/30">
                    Travel Collection
                  </Badge>
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {collection.name}
                  </h1>
                  {collection.description && (
                    <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                      {collection.description}
                    </p>
                  )}
                </div>
                
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  {collection.created_by_user && (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6 border border-gray-300">
                        <AvatarImage src={collection.created_by_user.avatar_url || ''} />
                        <AvatarFallback className="bg-primary/20 text-primary text-xs">
                          {collection.created_by_user.username?.[0]?.toUpperCase() || 'A'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">by {collection.created_by_user.username || 'Anonymous'}</span>
                    </div>
                  )}
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium">{actualPlacesCount} places</span>
                  </div>
                  {collection.city && (
                    <>
                      <span>•</span>
                      <span>{collection.city}{collection.country && `, ${collection.country}`}</span>
                    </>
                  )}
                  {collection.followers_count !== undefined && collection.followers_count > 0 && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{collection.followers_count} followers</span>
                      </div>
                    </>
                  )}
                  {collection.created_at && (
                    <>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(collection.created_at)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Featured Places Grid - Spotify Style */}
                {featuredPlaces.length > 0 && (
                  <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/50 shadow-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Star className="w-4 h-4 text-yellow-500" />
                      Featured Places
                    </h4>
                    <div className="space-y-2">
                      {featuredPlaces.slice(0, 4).map((place, index) => (
                        <div key={place.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/50 transition-colors">
                          <div className="w-10 h-10 bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
                            {place.photos?.[0] ? (
                              <img 
                                src={place.photos[0]} 
                                alt={place.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <MapPin className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-grow min-w-0">
                            <h5 className="text-sm font-medium text-gray-900 truncate">{place.name}</h5>
                            <p className="text-xs text-gray-600 capitalize truncate">
                              {place.place_type?.replace('_', ' ') || 'Place'}
                            </p>
                          </div>
                          {place.rating && (
                            <div className="flex items-center gap-1 text-xs">
                              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                              <span className="text-gray-600">{place.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      ))}
                      {hasMorePlaces && (
                        <div className="text-center pt-2">
                          <Badge variant="outline" className="text-xs">
                            +{actualPlacesCount - 4} more places
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Primary Share Button */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Share className="w-4 h-4" />
              Quick Share with Beautiful Visual
            </Label>
            <Button
              onClick={handleNativeShare}
              disabled={isGeneratingStoryImage}
              className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 text-white h-14 flex items-center gap-3 text-lg font-medium"
            >
              {isGeneratingStoryImage ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Creating Visual...</span>
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  <span>Share Collection</span>
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Creates a beautiful story image. For Instagram: Add a link sticker or mention "link in bio"
            </p>
          </div>

          {/* Quick Copy Link */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Or Copy Link Only
            </Label>
            <div className="flex gap-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1 font-mono text-sm bg-gray-50"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="flex items-center gap-2 px-4"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Social Media Options */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Share Directly to Platforms</Label>
            <div className="grid grid-cols-2 gap-3">
              {shareOptions.map((option) => (
                <Button
                  key={option.name}
                  variant="outline"
                  className={`${option.color} text-white border-0 h-12 justify-start gap-3 hover:scale-105 transition-all duration-200`}
                  disabled={isGeneratingStoryImage && (option.action === 'native' || option.action === 'reddit')}
                  onClick={() => {
                    if (option.action === 'native') {
                      handleNativeShare();
                    } else if (option.action === 'reddit') {
                      handleRedditShare();
                    } else if (option.url) {
                      window.open(option.url, '_blank', 'width=600,height=400');
                    }
                  }}
                >
                  {(isGeneratingStoryImage && (option.action === 'native' || option.action === 'reddit')) ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <option.icon className="w-5 h-5" />
                  )}
                  <span className="font-medium">{option.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Enhanced Social Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h4 className="font-semibold mb-3 text-sm text-gray-700">Social Media Preview</h4>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Preview Header with Collection Image */}
              <div className="aspect-video bg-gradient-to-br from-primary/20 to-purple-500/20 relative overflow-hidden">
                {featuredPlaces[0]?.photos?.[0] ? (
                  <img 
                    src={featuredPlaces[0].photos[0]} 
                    alt={collection.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <QrCode className="w-12 h-12 text-primary/60" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h4 className="font-bold text-white text-lg mb-1 truncate">
                    {collection.name}
                  </h4>
                  <div className="flex items-center gap-2 text-white/90 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{actualPlacesCount} places</span>
                    {collection.city && (
                      <>
                        <span>•</span>
                        <span>{collection.city}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Preview Content */}
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {shareDescription}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {collection.created_by_user?.username && (
                      <span>by {collection.created_by_user.username}</span>
                    )}
                  </div>
                  <p className="text-xs text-blue-600 font-medium">
                    travilis.com
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              onClick={handleNativeShare}
              disabled={isGeneratingStoryImage}
              className="flex-1 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
            >
              {isGeneratingStoryImage ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Share className="w-4 h-4 mr-2" />
                  Share Collection
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onClose} className="px-6">
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
